import { Dialog, DialogTitle } from "@mui/material";
import { useLocalStorage } from "hooks/use-local-storage";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { links } from "@/data/links";
import { useLogout } from "@/features/auth/shared/services/logout";
import { useLang } from "@/hooks/use-lang";
import { useUpdateBrowserToken } from "@/services/browser-token/update-browser-token";
import { useBrowserToken } from "@/services/browser-token/use-browser-token";
import { useValidateBrowserToken } from "@/services/browser-token/validate-browser-token";

import { Button } from "ui/core/button";

import { LinkableMarkdown } from "../../markdown/LinkableMarkdown";

export const MultiDeviceConfirmationDialog = () => {
  const { t } = useLang();
  const { browserToken } = useBrowserToken();
  const { data, isLoading, isError } = useValidateBrowserToken({
    browserToken: browserToken ?? "",
  });
  const [open, setOpen] = useState(false);

  const { mutateAsync: updateBrowserToken } = useUpdateBrowserToken({
    browserToken: browserToken ?? "",
  });
  const onConfirmConnection = async () => {
    const { message } = await updateBrowserToken({
      browserToken: browserToken ?? "",
    });
    if (message === "Success") {
      setOpen(false);
    }
  };

  const { mutate: logout } = useLogout();

  // Create a ref for the dialog element
  const [isCI] = useLocalStorage("CI", undefined);
  const searchParams = useSearchParams();
  const showDialogWhenCI = searchParams.get("showDialogWhenCI") === "true";

  useEffect(() => {
    if (isCI && !showDialogWhenCI) return;
    if (!data) return;
    if (!!data.confirm && data.confirm === "Fail") {
      setOpen(true);
    } else {
      setOpen(false);
    }

    return () => {
      setOpen(false);
    };
  }, [data, isCI, showDialogWhenCI]);

  if (isLoading || isError) return null;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      PaperProps={{
        style: {
          borderRadius: 15,
        },
      }}
      className="z-[99999]"
    >
      <DialogTitle justifyContent="center">
        {t.auth.multiDevices.title}
      </DialogTitle>
      <div className="flex flex-col justify-center space-y-4 px-6 pb-4">
        <LinkableMarkdown
          links={[{ text: t.auth.multiDevices.contact, href: links.contact }]}
          className="text-gray-700 [&_a]:text-sky-700 [&_a]:no-underline"
        >
          {t.auth.multiDevices.description}
        </LinkableMarkdown>
        {data && (
          <div className="rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 font-medium" aria-label="latest-device">
              {t.auth.multiDevices.latestDevice}
            </h4>
            <div className="space-y-2 text-sm">
              <p aria-label="ip">
                <span className="font-medium">{t.auth.multiDevices.ip}:</span>{" "}
                {data.ip}
              </p>
              <p aria-label="device">
                <span className="font-medium">
                  {t.auth.multiDevices.device}:
                </span>{" "}
                {data.device}
              </p>
              <p aria-label="browser">
                <span className="font-medium">
                  {t.auth.multiDevices.browser}:
                </span>{" "}
                {data.browser}
              </p>
            </div>
          </div>
        )}
        <p className="text-sm text-gray-700">
          {t.auth.multiDevices.allowConnection}
        </p>

        <div className="mt-6 flex justify-center gap-5">
          <Button
            variant="secondary"
            onClick={() => logout()}
            className="w-full max-w-xs"
          >
            {t.auth.multiDevices.logout}
          </Button>
          <Button
            variant="primary"
            onClick={onConfirmConnection}
            className="w-full max-w-xs"
          >
            {t.auth.multiDevices.confirmConnection}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
